import { ThemePalette } from 'mui.theme';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
  ResponsiveContainer,
  Brush,
} from 'recharts';
import { formatYAxis } from './Chart';
import { truncateTo2dp } from 'utils/helpers/general';
import { interpolateDataPoints } from '../../utils';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_TIME_FORMAT_SECONDS } from 'utils/constants/dateTime';
import useAppTheme from 'hooks/useAppTheme';
import { memo } from 'react';
import { alpha } from '@mui/material/styles';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

const _palette = clientThemePalette[CLIENT_THEME];

export const IndividualAreaChart = ({
  data,
  dataType,
  unit,
  selectedOperation,
  startDate,
  endDate,
}: {
  data: any;
  dataType: string;
  unit: string;
  selectedOperation: string;
  startDate: any;
  endDate: any;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const formatTimestamp = (timestamp: number): string => {
    const date = convertUtcToDisplayTime(
      timestamp,
      preferredTimeDisplayMode,
      timezone,
      dataType === 'highGrain' ? 'HH:mm:ss' : 'YY MMM DD'
    );
    return date;
  };

  let chartData = data;

  if (data.length > 0) {
    const deviceSensor = data[0].device_sensor;
    if (['SbIgnitionStatus'].includes(deviceSensor)) {
      chartData = interpolateDataPoints(data, 120000, startDate, endDate);
    }
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        width={500}
        height={300}
        data={chartData}
        margin={{
          top: 30,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={
            enableDarkTheme
              ? _palette.dark.borderGrey
              : _palette.light.borderGrey
          }
          vertical={false}
        />
        <XAxis
          dataKey="ts"
          allowDuplicatedCategory={false}
          tickFormatter={formatTimestamp}
          textAnchor="end"
          tick={{ fontSize: 11 }}
        />
        <YAxis
          tickFormatter={formatYAxis}
          label={{
            value: `${unit}`,
            angle: 270,
            position: 'insideLeft',
            fontSize: 10,
            textAnchor: 'middle',
            dy: 0,
            dx: -5,
          }}
          tick={{ fontSize: 11 }}
        />
        <Tooltip
          cursor={{
            fill: enableDarkTheme
              ? alpha(_palette.dark.primary, 0.1)
              : 'rgba(0,0,0,0.05)',
          }}
          wrapperStyle={{ outline: 'none', fontSize: '11px' }}
          contentStyle={{
            backgroundColor: enableDarkTheme ? '#2D3748' : '#FFFFFF',
          }}
          itemStyle={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
          content={
            <CustomTooltip unit={unit} selectedOperation={selectedOperation} />
          }
        />
        <Area
          dataKey={selectedOperation}
          stroke={
            enableDarkTheme ? _palette.dark.primary : _palette.light.primary
          }
          fill={alpha(
            enableDarkTheme ? _palette.dark.primary : _palette.light.primary,
            0.25
          )}
          // activeDot={{ r: 3 }}
          // dot={{
          //   fill: enableDarkTheme
          //     ? (enableDarkTheme ? _palette.dark : _palette.light).boxBackground
          //     : (enableDarkTheme ? _palette.dark : _palette.light).boxBackground,
          //   strokeWidth: 1,
          //   r: 2,
          // }}
        />
        {dataType === 'highGrain' && (
          <Brush
            dataKey="ts"
            height={30}
            data={chartData}
            tickFormatter={formatTimestamp}
            fill={
              enableDarkTheme
                ? _palette.dark.boxBackground
                : _palette.light.boxBackground
            }
          >
            <AreaChart>
              <Area
                dataKey={selectedOperation}
                type="linear"
                stroke={
                  enableDarkTheme
                    ? _palette.dark.primary
                    : _palette.light.primary
                }
                fill={alpha(
                  enableDarkTheme
                    ? _palette.dark.primary
                    : _palette.light.primary,
                  0.2
                )}
                isAnimationActive={false}
              />
            </AreaChart>
          </Brush>
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const CustomTooltip = memo(
  ({ active, payload, selectedOperation }: any) => {
    const { t } = useTranslation();
    const { enableDarkTheme } = useAppTheme();
    const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

    if (active && payload && payload.length) {
      const { ts } = payload[0].payload;
      const value = payload[0].payload[selectedOperation];

      return (
        <div
          style={{
            background: 'transparent',
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
            fontSize: '11px',
            outline: 'none',
          }}
        >
          <p>{`${t('chart_date_label')} ${convertUtcToDisplayTime(
            ts,
            preferredTimeDisplayMode,
            timezone,
            DATE_TIME_FORMAT_SECONDS
          )}`}</p>
          <p>{`${t('value')} ${formatYAxis(truncateTo2dp(value))}`}</p>
        </div>
      );
    }
    return null;
  }
);

CustomTooltip.displayName = 'CustomTooltip';
